import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Upload,
  Copy,
  Trash2,
  Search,
  Image as ImageIcon,
  CheckCircle,
  X,
  Download,
} from 'lucide-react';
import { adminThoughtsApi } from '../services/api';
import { toast } from 'sonner';

interface LibraryImage {
  id: string;
  url: string;
  filename: string;
  thoughtId: string; // draft thought created to store the image
  uploadedAt: string;
}

const STORAGE_KEY = 'totd_image_library';

function loadFromStorage(): LibraryImage[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToStorage(images: LibraryImage[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(images));
}

export default function ImageLibrary() {
  const [images, setImages] = useState<LibraryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<LibraryImage | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch all image library entries from the server on mount
  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);
      try {
        let allThoughts: any[] = [];
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const result = await adminThoughtsApi.getAll({
            status: 'draft',
            search: 'Image Library',
            limit: 100,
            page,
          });
          const thoughts = (result.thoughts || []).filter(
            (t: any) => t.text?.includes('[Image Library]')
          );
          allThoughts = [...allThoughts, ...thoughts];
          totalPages = result.totalPages || 1;
          page++;
        }

        const libraryImages: LibraryImage[] = allThoughts
          .filter((t: any) => t.imageUrl)
          .map((t: any) => ({
            id: t._id,
            url: t.imageUrl,
            filename: t.text?.replace(/^\[Image Library\]\s*/, '') || t._id,
            thoughtId: t._id,
            uploadedAt: t.createdAt,
          }));

        // Sort newest first
        libraryImages.sort(
          (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
        );

        setImages(libraryImages);
        saveToStorage(libraryImages);
      } catch (err) {
        console.error('ImageLibrary fetch error:', err);
        setImages(loadFromStorage());
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, []);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const uploaded: LibraryImage[] = [];

    setIsUploading(true);
    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported image type.`);
        continue;
      }
      try {
        // Upload via the existing thoughts endpoint as a draft
        const formData = new FormData();
        formData.append('text', `[Image Library] ${file.name}`);
        formData.append('author', 'Image Library');
        formData.append('category', 'General');
        formData.append('status', 'draft');
        formData.append('image', file);

        const result = await adminThoughtsApi.create(formData);
        const thought = result.thought || result.data || result;

        if (thought?.imageUrl) {
          uploaded.push({
            id: thought._id,
            url: thought.imageUrl,
            filename: file.name,
            thoughtId: thought._id,
            uploadedAt: new Date().toISOString(),
          });
        } else {
          toast.error(`Could not retrieve URL for ${file.name}.`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}.`);
      }
    }
    setIsUploading(false);

    if (uploaded.length > 0) {
      setImages(prev => [...uploaded, ...prev]);
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded successfully.`);
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (image: LibraryImage) => {
    if (!confirm(`Remove "${image.filename}" from the library?`)) return;
    try {
      // Delete the backing draft thought from the server
      await adminThoughtsApi.delete(image.thoughtId);
    } catch {
      // If already deleted on server, just remove locally
    }
    setImages(prev => prev.filter(img => img.id !== image.id));
    if (previewImage?.id === image.id) setPreviewImage(null);
    toast.success('Image removed.');
  };

  const copyUrl = (image: LibraryImage) => {
    navigator.clipboard.writeText(image.url);
    setCopiedId(image.id);
    toast.success('URL copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadUrlsExcel = () => {
    const data = filteredImages.map((img, index) => ({
      '#': index + 1,
      Filename: img.filename,
      'Image URL': img.url,
      'Uploaded At': new Date(img.uploadedAt).toLocaleString(),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 80 }, { wch: 20 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Image URLs');
    XLSX.writeFile(workbook, `image_urls_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const filteredImages = images.filter(img =>
    img.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-slate-800 mb-2">Image Library</h2>
        <p className="text-slate-600">Upload images and copy their URLs for use in the Excel bulk upload</p>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleUpload(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-[#2B7A7E] bg-[#2B7A7E]/5'
            : 'border-slate-300 hover:border-[#2B7A7E] hover:bg-slate-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-[#2B7A7E] border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-600 font-medium">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 bg-[#2B7A7E]/10 rounded-full flex items-center justify-center">
              <Upload className="w-7 h-7 text-[#2B7A7E]" />
            </div>
            <div>
              <p className="font-semibold text-slate-700">Drop images here or click to upload</p>
              <p className="text-sm text-slate-500 mt-1">Supports JPG, PNG, WebP, GIF — multiple files allowed</p>
            </div>
          </div>
        )}
      </div>

      {/* Search + Count */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
          />
        </div>
        <p className="text-sm text-slate-500 whitespace-nowrap">
          {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={downloadUrlsExcel}
          disabled={filteredImages.length === 0}
          className="px-4 py-2.5 bg-[#D4AF37] text-white rounded-xl hover:bg-[#bf9a2d] transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          Download URLs
        </button>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <div className="w-10 h-10 border-4 border-[#2B7A7E] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="font-medium">Loading images...</p>
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <ImageIcon className="w-16 h-16 mb-4 opacity-30" />
          <p className="font-medium">{searchQuery ? 'No images match your search' : 'No images uploaded yet'}</p>
          {!searchQuery && <p className="text-sm mt-1">Upload images above to get started</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredImages.map(image => (
            <div
              key={image.id}
              className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Thumbnail */}
              <div
                className="aspect-square cursor-pointer overflow-hidden bg-slate-100"
                onClick={() => setPreviewImage(image)}
              >
                <img
                  src={image.url}
                  alt={image.filename}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>

              {/* Filename */}
              <div className="px-3 py-2">
                <p className="text-xs text-slate-500 truncate" title={image.filename}>{image.filename}</p>
              </div>

              {/* Actions */}
              <div className="px-3 pb-3 flex gap-2">
                <button
                  onClick={() => copyUrl(image)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-[#2B7A7E] text-white text-xs rounded-lg hover:bg-[#1e5a5d] transition-colors"
                >
                  {copiedId === image.id
                    ? <><CheckCircle className="w-3.5 h-3.5" /> Copied</>
                    : <><Copy className="w-3.5 h-3.5" /> Copy URL</>
                  }
                </button>
                <button
                  onClick={() => handleDelete(image)}
                  className="p-1.5 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <p className="font-semibold text-slate-800 truncate">{previewImage.filename}</p>
              <button
                onClick={() => setPreviewImage(null)}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5">
              <img
                src={previewImage.url}
                alt={previewImage.filename}
                className="w-full rounded-xl object-contain max-h-[60vh]"
              />
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <input
                readOnly
                value={previewImage.url}
                className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 font-mono"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={() => copyUrl(previewImage)}
                className="px-4 py-2.5 bg-[#2B7A7E] text-white rounded-lg hover:bg-[#1e5a5d] transition-colors flex items-center gap-2 text-sm font-medium"
              >
                {copiedId === previewImage.id
                  ? <><CheckCircle className="w-4 h-4" /> Copied!</>
                  : <><Copy className="w-4 h-4" /> Copy URL</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
